import { Close } from "@mui/icons-material";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import {
  EditFormMode,
  formatRebuildRouteAppliedInfo,
  IgnoreMLPathReasonEnum,
  Log,
  onActivateKeyDown,
  RebuildRouteTypeEnum,
  resolveDirectionsServiceLabel,
  resolveInitialRebuildRouteProvider,
  resolveRebuildRouteAppliedMlPathId,
  RouteRebuildSourceEnum,
  useDrawers,
  useFormats,
  useHaveCustomPermissions,
  type DirectionsServiceEnum,
} from "@ryde/shared";

interface IRebuildRouteDropdownAppliedInfoProps {
  lastUsedProvider?: DirectionsServiceEnum | null;
  systemDefaultProvider: DirectionsServiceEnum;
  routeRebuildAt?: TDateISO;
  sessionRouteRebuildAt?: TDateISO | null;
  rebuildRouteType?: RebuildRouteTypeEnum | null;
  lastSelectedRebuildType?: RebuildRouteTypeEnum | null;
  usedCachedRouteId?: number | null;
  customStationRouteId?: number | null;
  routeRebuildSource?: RouteRebuildSourceEnum | null;
  ignoreMLPathReason?: IgnoreMLPathReasonEnum | null;
  rideOpenerId?: number;
  onClose?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const RebuildRouteDropdownAppliedInfo: FC<
  IRebuildRouteDropdownAppliedInfoProps
> = ({
  lastUsedProvider,
  systemDefaultProvider,
  routeRebuildAt,
  sessionRouteRebuildAt,
  rebuildRouteType,
  lastSelectedRebuildType,
  usedCachedRouteId,
  customStationRouteId,
  routeRebuildSource,
  ignoreMLPathReason,
  rideOpenerId,
  onClose,
}) => {
  const { t } = useTranslation();
  const { formatDateTimeWithSecondsNormalized } = useFormats();
  const { hasReadStationRoutePermission } = useHaveCustomPermissions();
  const { openStationRouteEditFormDrawer } = useDrawers();
  const provider = resolveInitialRebuildRouteProvider({
    lastUsedProvider,
    systemDefaultProvider,
  });
  const providerLabel = resolveDirectionsServiceLabel(provider) ?? provider;
  const effectiveRouteRebuildAt =
    routeRebuildAt ?? sessionRouteRebuildAt ?? null;
  const appliedAtLabel = effectiveRouteRebuildAt
    ? formatDateTimeWithSecondsNormalized(effectiveRouteRebuildAt)
    : undefined;
  const effectiveRebuildRouteType =
    lastSelectedRebuildType ?? rebuildRouteType ?? null;
  const appliedMlPathId = resolveRebuildRouteAppliedMlPathId({
    usedCachedRouteId,
    customStationRouteId,
    routeRebuildSource,
    ignoreMLPathReason,
  });
  const canOpenMlPath =
    hasReadStationRoutePermission && appliedMlPathId !== null;

  const handleOpenMlPath = async (): Promise<void> => {
    if (appliedMlPathId === null) {
      return;
    }

    const result = await openStationRouteEditFormDrawer({
      id: appliedMlPathId,
      rideOpenerId,
      mode: EditFormMode.UnboundDrawer,
    });

    if (result.success) {
      Log.debug("Station route updated");
    }
  };

  return (
    <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--info">
      <div className="rebuild-route-dropdown-applied">
        <span className="rebuild-route-dropdown-applied-label">
          {formatRebuildRouteAppliedInfo({
            providerLabel,
            provider,
            appliedAtLabel,
            rebuildRouteType: effectiveRebuildRouteType,
            routeRebuildSource,
            t,
          })}
        </span>
        {appliedMlPathId !== null && (
          <span
            className="rebuild-route-dropdown-applied-ml-path"
            data-e2e="rebuild-route-applied-ml-path"
            role={canOpenMlPath ? "button" : undefined}
            tabIndex={canOpenMlPath ? 0 : undefined}
            onClick={
              canOpenMlPath
                ? () => {
                    void handleOpenMlPath();
                  }
                : undefined
            }
            onKeyDown={
              canOpenMlPath
                ? onActivateKeyDown(() => {
                    void handleOpenMlPath();
                  })
                : undefined
            }
          >
            {t("general.REBUILD_ROUTE_APPLIED_CACHED_PATH", {
              id: appliedMlPathId,
            })}
          </span>
        )}
        {onClose && (
          <button
            type="button"
            className="rebuild-route-dropdown-close"
            data-e2e="rebuild-route-dropdown-close"
            aria-label={t("general.CLOSE")}
            onClick={onClose}
          >
            <Close fontSize="inherit" />
          </button>
        )}
      </div>
    </div>
  );
};
